
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Bell, AlertCircle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LeadStageType } from "@/context/LeadContext";

interface LeadStatusReminderBadgeProps {
  lastUpdated: string;
  stage: LeadStageType;
  className?: string;
}

const LeadStatusReminderBadge: React.FC<LeadStatusReminderBadgeProps> = ({
  lastUpdated,
  stage,
  className
}) => {
  const lastUpdatedDate = new Date(lastUpdated);
  const now = new Date();
  const hoursSinceUpdate = Math.floor((now.getTime() - lastUpdatedDate.getTime()) / (1000 * 60 * 60));
  
  // If the lead is already marked as completed or information was provided, no reminder needed
  if (stage === "έκλεισεΗΔουλειά" || stage === "ήθελεΠληροφορίες") {
    return null;
  }

  // If the lead is still new (not in "pending" state) and it's been more than 24 hours
  if (stage === "Νέο" && hoursSinceUpdate >= 24) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge 
              variant="destructive" 
              className={cn("flex items-center gap-1 animate-pulse", className)}
            >
              <AlertCircle className="h-3 w-3" />
              <span>Επείγον</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Έχουν περάσει {hoursSinceUpdate} ώρες χωρίς καμία ενέργεια!</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // If the lead is still new and it's been more than 4 hours but less than 24
  if (stage === "Νέο" && hoursSinceUpdate >= 4) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge 
              variant="warning" 
              className={cn("flex items-center gap-1", className)}
            >
              <Bell className="h-3 w-3" />
              <span>Εκκρεμεί</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Έχουν περάσει {hoursSinceUpdate} ώρες από την καταχώρηση.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // If the lead is in pending state for more than 48 hours
  if (stage === "σεΕκκρεμότητα" && hoursSinceUpdate >= 48) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge 
              variant="warning" 
              className={cn("flex items-center gap-1", className)}
            >
              <Clock className="h-3 w-3" />
              <span>Ενημέρωση</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Το lead είναι σε εκκρεμότητα για {Math.floor(hoursSinceUpdate / 24)} ημέρες.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return null;
};

export default LeadStatusReminderBadge;
